import React, { useCallback, useMemo } from 'react';

import { IChangeEvent } from '@rjsf/core';
import Form from '@rjsf/mui';
import { RJSFSchema } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import { useSnackbar } from 'notistack';

import { Box } from '@mui/material';

import VendorsService from '~/services/VendorsService';

import { enhanceErrorPaymentSchema, errorPaymentUiSchema, errorPaymentWidgets } from './constants';

interface Props {
  schema: RJSFSchema;
  offerId: string;
  vendorId: string;
  onErrorPaymentAdded: (errorPayment: Record<string, unknown>) => void;
}

export function ErrorPaymentsAddForm({ schema, offerId, vendorId, onErrorPaymentAdded }: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const enhancedSchema = useMemo(() => enhanceErrorPaymentSchema(schema), [schema]);

  const formData = useMemo(
    () => ({
      date_of_adjustment: new Date().toISOString().slice(0, 10),
      fk_offer: offerId,
      fk_vendor: vendorId,
      currency: 'AUD',
      amount: '0',
      le_booking_number: '',
      reason: '',
      description: '',
    }),
    [offerId, vendorId],
  );

  const validationHandler = useCallback((data, errors) => {
    const amount = Number.parseFloat(data.amount);
    if (!Number.isFinite(amount)) {
      errors.amount.addError('should be a number');
    }
    return errors;
  }, []);

  const submitHandler = useCallback(
    async (e: IChangeEvent) => {
      try {
        const res = await VendorsService.createErrorPayment(e.formData);
        enqueueSnackbar('Error payment saved', { variant: 'success' });
        onErrorPaymentAdded(res.result);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        enqueueSnackbar(`Can't save error payment: ${errorMessage}`, { variant: 'error' });
      }
    },
    [onErrorPaymentAdded, enqueueSnackbar],
  );

  return (
    <Box id="errorpayments-form" mb={2}>
      <Form
        schema={enhancedSchema}
        uiSchema={errorPaymentUiSchema}
        widgets={errorPaymentWidgets}
        onSubmit={submitHandler}
        customValidate={validationHandler}
        validator={validator}
        formData={formData}
      />
    </Box>
  );
}
